import { useState } from 'react';
import { HandsClapping, Briefcase, PaperPlaneRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import type { Boxer, Sponsor } from '@/types/boxer';
import type { FightRequest, SponsorRequest } from '@/types/request';

interface RequestFormProps {
  currentUser: any;
  boxers: Boxer[];
  sponsors: Sponsor[];
  allBoxers: Boxer[];
  onSubmitFightRequest: (request: FightRequest) => void;
  onSubmitSponsorRequest: (request: SponsorRequest) => void;
}

export function RequestForm({ currentUser, boxers, sponsors, allBoxers, onSubmitFightRequest, onSubmitSponsorRequest }: RequestFormProps) {
  const [requestType, setRequestType] = useState<'fight' | 'sponsor'>('fight');
  const [selectedOpponent, setSelectedOpponent] = useState('');
  const [selectedSponsor, setSelectedSponsor] = useState('');
  const [message, setMessage] = useState('');
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [selectedBoxerStateId, setSelectedBoxerStateId] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const handleInitialSubmit = () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    // Show contact dialog for fight requests
    if (requestType === 'fight') {
      setShowContactDialog(true);
    } else {
      // Sponsor requests don't need contact info
      handleFinalSubmit();
    }
  };

  const handleFinalSubmit = () => {
    if (requestType === 'fight') {
      // Validate contact info for fight requests
      if (!selectedBoxerStateId || selectedBoxerStateId === 'none') {
        toast.error('Please select which boxer you are');
        return;
      }

      const selectedBoxer = allBoxers.find(b => b.stateId === selectedBoxerStateId);
      const opponent = (selectedOpponent && selectedOpponent !== 'none') ? boxers.find(b => b.id === selectedOpponent) : undefined;
      
      const request: FightRequest = {
        id: `fight-req-${Date.now()}`,
        boxerId: selectedBoxer?.id || currentUser?.linkedEntityId || currentUser?.id || 'unknown',
        boxerName: selectedBoxer ? `${selectedBoxer.firstName} ${selectedBoxer.lastName}` : 'Unknown',
        opponentId: opponent?.id,
        opponentName: opponent ? `${opponent.firstName} ${opponent.lastName}` : undefined,
        message: contactPhone.trim() 
          ? `${message.trim()}\n\nContact Phone: ${contactPhone.trim()}`
          : message.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
      };

      onSubmitFightRequest(request);
      toast.success('Fight request submitted successfully!');
      
      // Close dialog and reset form
      setShowContactDialog(false);
      setSelectedBoxerStateId('');
      setContactPhone('');
      setSelectedOpponent('');
      setMessage('');
    } else {
      const sponsor = (selectedSponsor && selectedSponsor !== 'none') ? sponsors.find(s => s.id === selectedSponsor) : undefined;
      
      const request: SponsorRequest = {
        id: `sponsor-req-${Date.now()}`,
        boxerId: currentUser?.linkedEntityId || currentUser?.id || 'unknown',
        boxerName: currentUser?.username || 'Unknown User',
        sponsorId: sponsor?.id,
        sponsorName: sponsor?.name,
        message: message.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
      };

      onSubmitSponsorRequest(request);
      toast.success('Sponsorship request submitted successfully!');
      
      // Reset form
      setSelectedSponsor('');
      setMessage('');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl uppercase text-secondary tracking-wide">
          Submit a Request
        </h2>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Request a fight or sponsorship opportunity
        </p>
      </div>

      <Card className="p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <div>
            <Label>Request Type</Label>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant={requestType === 'fight' ? 'default' : 'outline'}
                onClick={() => setRequestType('fight')}
                className="flex-1 flex items-center gap-2"
              >
                <HandsClapping className="w-4 h-4" />
                Fight Request
              </Button>
              <Button
                type="button"
                variant={requestType === 'sponsor' ? 'default' : 'outline'}
                onClick={() => setRequestType('sponsor')}
                className="flex-1 flex items-center gap-2"
              >
                <Briefcase className="w-4 h-4" />
                Sponsorship Request
              </Button>
            </div>
          </div>

          {requestType === 'fight' && (
            <div>
              <Label htmlFor="opponent">Preferred Opponent (Optional)</Label>
              <Select value={selectedOpponent} onValueChange={setSelectedOpponent}>
                <SelectTrigger id="opponent" className="mt-1">
                  <SelectValue placeholder="Select an opponent or leave blank for any match" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any Available Opponent</SelectItem>
                  {boxers.filter(b => b.id !== currentUser?.linkedEntityId).map(boxer => (
                    <SelectItem key={boxer.id} value={boxer.id}>
                      {boxer.firstName} {boxer.lastName} ({boxer.wins}W-{boxer.losses}L)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {requestType === 'sponsor' && (
            <div>
              <Label htmlFor="sponsor">Preferred Sponsor (Optional)</Label>
              <Select value={selectedSponsor} onValueChange={setSelectedSponsor}>
                <SelectTrigger id="sponsor" className="mt-1">
                  <SelectValue placeholder="Select a sponsor or leave blank for any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any Available Sponsor</SelectItem>
                  {sponsors.map(sponsor => (
                    <SelectItem key={sponsor.id} value={sponsor.id}>
                      {sponsor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                requestType === 'fight'
                  ? 'Explain why you want this fight, your current training status, and when you\'re available...'
                  : 'Explain why you\'re seeking sponsorship and what you can offer...'
              }
              className="mt-1 min-h-[120px]"
            />
          </div>

          <Button onClick={handleInitialSubmit} size="lg" className="w-full sm:w-auto">
            <PaperPlaneRight className="w-4 h-4 mr-2" />
            Submit Request
          </Button>
        </div>
      </Card>

      {/* Contact Information Dialog for Fight Requests */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Contact Information Required</DialogTitle>
            <DialogDescription>
              Please provide your contact information so we can reach you about this fight request.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-4 py-4">
            <div>
              <Label htmlFor="boxer-stateid">Which Boxer Are You? *</Label>
              <Select value={selectedBoxerStateId} onValueChange={setSelectedBoxerStateId}>
                <SelectTrigger id="boxer-stateid" className="mt-1">
                  <SelectValue placeholder="Select your State ID" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a State ID</SelectItem>
                  {allBoxers.map(boxer => (
                    <SelectItem key={boxer.id} value={boxer.stateId}>
                      {boxer.stateId} - {boxer.firstName} {boxer.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="contact-phone">Phone Number (Optional)</Label>
              <Input
                id="contact-phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="Enter your phone number"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowContactDialog(false);
                setSelectedBoxerStateId('');
                setContactPhone('');
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleFinalSubmit}
              className="w-full sm:w-auto"
            >
              Submit Fight Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
